import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { RequestStage } from '@/lib/types'

export type BadgeTone = 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info'

/* Squared, not pilled: board labels are printed on rectangular flaps. */
const TONES: Record<BadgeTone, string> = {
  neutral: 'border-line-strong bg-elevated text-muted',
  accent: 'border-accent/40 bg-accent-dim text-accent',
  ok: 'border-ok/40 bg-ok-dim text-ok',
  warn: 'border-warn/40 bg-warn-dim text-warn',
  danger: 'border-danger/40 bg-danger-dim text-danger',
  info: 'border-info/40 bg-elevated text-info',
}

export function Badge({
  tone = 'neutral',
  children,
  className = '',
}: {
  tone?: BadgeTone
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-[3px] border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${TONES[tone]} ${className}`}
    >
      {children}
    </span>
  )
}

/*
 * Board vocabulary. Kept to one plain word per stage so the flap reads at a
 * glance from arm's length — the label is doing the whole job of saying where
 * something is, and a phrase would not fit the flap or the glance.
 */
const STAGE_META: Record<RequestStage, { label: string; tone: BadgeTone; lamp: string }> = {
  pending: { label: 'Pending', tone: 'warn', lamp: 'bg-warn' },
  approved: { label: 'Approved', tone: 'info', lamp: 'bg-info' },
  declined: { label: 'Declined', tone: 'neutral', lamp: 'bg-faint' },
  downloading: { label: 'Loading', tone: 'accent', lamp: 'bg-accent' },
  available: { label: 'Ready', tone: 'ok', lamp: 'bg-ok' },
  failed: { label: 'Failed', tone: 'danger', lamp: 'bg-danger' },
}

const FLAP_TEXT: Record<BadgeTone, string> = {
  neutral: 'text-muted',
  accent: 'text-accent',
  ok: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
  info: 'text-info',
}

/**
 * The split-flap status cell — the one place this interface spends its
 * boldness.
 *
 * It flips when the stage actually changes, and (via `delayMs`) staggers on
 * mount so a list of requests sets itself row by row the way a Solari board
 * does. The mount stagger matters more than the change animation in practice:
 * stages move on a two-minute cron, so a user is far more likely to see the
 * board set than to be watching when a single row turns over.
 */
export function StageBadge({ stage, delayMs = 0 }: { stage: RequestStage; delayMs?: number }) {
  const meta = STAGE_META[stage] ?? { label: stage, tone: 'neutral' as const, lamp: 'bg-faint' }

  // Re-keying the element restarts the CSS animation; tracking the previous
  // stage in a ref keeps the first render from counting as a change, so rows
  // do not flip merely because they mounted.
  const previous = useRef(stage)
  const [flipKey, setFlipKey] = useState(0)

  useEffect(() => {
    if (previous.current !== stage) {
      previous.current = stage
      setFlipKey((key) => key + 1)
    }
  }, [stage])

  return (
    <span
      key={flipKey}
      className={`flap flap-animate signage inline-flex items-center gap-1.5 rounded-[3px] px-2 py-1 text-[10px] leading-none ${FLAP_TEXT[meta.tone]}`}
      style={{ animationDelay: `${delayMs}ms`, animationFillMode: 'backwards' }}
    >
      <span aria-hidden className={`size-1.5 rounded-full ${meta.lamp}`} />
      {meta.label}
    </span>
  )
}
